//#region imports
import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { createAdminAccount, selectAdminStatus, selectAdminError } from '_redux/slices/profile/staff-management';
//#endregion

const ORG_TYPES = [
    { value: 'AIRPORT', label: 'Airport' },
    { value: 'AIRLINE', label: 'Airline' },
    { value: 'RETAILER', label: 'Retailer' },
    { value: 'TRAVEL_AGENT', label: 'Travel Agent' },
];

const CreateAdminForm = () => {

    const dispatch = useDispatch();

    const adminStatus = useSelector(selectAdminStatus);
    const adminError = useSelector(selectAdminError);

    const [formData, setFormData] = useState({ name: '', email: '', orgType: 'AIRPORT', authority: '' });
    const [validationErrors, setValidationErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!formData.authority.trim()) {
            errors.authority = 'IATA code / organisation identifier is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage('');

        if (!validateForm()) return;

        try {
            await dispatch(createAdminAccount({
                name: formData.name.trim(),
                email: formData.email.trim(),
                orgType: formData.orgType,
                authority: formData.authority.trim().toUpperCase(),
            })).unwrap();

            setSuccessMessage(`Admin account created for ${formData.email}. A welcome email with a temporary password has been sent.`);
            setFormData({ name: '', email: '', orgType: 'AIRPORT', authority: '' });
        } catch (error) {
            // error surfaced via adminError selector
        }
    };

    return (
        <Container
            style={{
                boxShadow: '0px 4px 8px 2px #9E9E9E',
                borderRadius: '8px',
                padding: '24px',
                width: '100%',
                marginBottom: '40px',
                marginTop: '20px',
            }}
        >
            <p style={{ fontSize: 22, fontWeight: 'bold' }}>Create Organization Admin</p>
            <p style={{ fontSize: 16, fontWeight: '600', opacity: 0.6 }}>
                Creates a new organisation admin account. The admin will receive a welcome
                email with a temporary password and must verify their email, change the
                password and set up multi-factor authentication before accessing the portal.
                They can then invite their own staff from the Staff Management page.
            </p>

            {adminStatus === 'failed' && adminError && (
                <Alert variant="danger">{adminError}</Alert>
            )}
            {successMessage && (
                <Alert variant="success">{successMessage}</Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="Enter full name"
                                value={formData.name}
                                onChange={handleInputChange}
                                isInvalid={!!validationErrors.name}
                                disabled={adminStatus === 'loading'}
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={handleInputChange}
                                isInvalid={!!validationErrors.email}
                                disabled={adminStatus === 'loading'}
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.email}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Organisation Type</Form.Label>
                            <Form.Select
                                name="orgType"
                                value={formData.orgType}
                                onChange={handleInputChange}
                                disabled={adminStatus === 'loading'}
                            >
                                {ORG_TYPES.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>IATA Code / Organisation ID</Form.Label>
                            <Form.Control
                                type="text"
                                name="authority"
                                placeholder="e.g. DUB"
                                value={formData.authority}
                                onChange={handleInputChange}
                                isInvalid={!!validationErrors.authority}
                                disabled={adminStatus === 'loading'}
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.authority}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Button variant="primary" type="submit" disabled={adminStatus === 'loading'}>
                    {adminStatus === 'loading' ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Creating...
                        </>
                    ) : 'Create Admin Account'}
                </Button>
            </Form>
        </Container>
    );
};

export default CreateAdminForm;
